import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ACTIONS_COL_WIDTH, Alert, DataTable, IdLink, RowActions, type DataTableColumn } from '@/components/ui';
import { formatAnchorValue, methodLabel } from '../../hired-equipment/utils/buildAnchor';
import { formatCurrency } from '../../hired-equipment/utils/gridMath';
import type { HiredEquipmentAnchor } from '../../hired-equipment/types';
import HeConfirmModal from '../../hired-equipment/components/HeConfirmModal';
import {
  discardHiredEquipmentDraft,
  listHiredEquipmentDrafts,
  type HiredEquipmentDraftSummary,
} from '../../../services/hiredEquipmentService';
import { describeLoadError } from '../../../utils/loadError';
import { formatDate as formatPacificDate } from '../../../utils/displayDateTime';
import FiltersCard from '../../../components/FiltersCard';
import { usePersistedListState } from '../../../hooks/usePersistedListState';

// Drafts tab on Submissions (V2-038). The signed-in user's saved hired equipment drafts, from the
// server (H14) – never from browser storage. Submitted forms are on the Submissions tab; "Submitted
// by Me" narrows it to the user's own.

const formatDate = (iso: string | null | undefined) => (iso ? formatPacificDate(iso) : '–');

const HiredEquipmentDrafts: React.FC = () => {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState<HiredEquipmentDraftSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [discardingId, setDiscardingId] = useState<string | null>(null);
  const [confirmDiscardId, setConfirmDiscardId] = useState<string | null>(null);
  // V3-028 / SRCH-01: the always-visible search in the filter header, kept for the sign-in
  // (FLT-09). A user's own drafts are few and loaded whole, so it narrows them in place.
  const [search, setSearch] = usePersistedListState('submissionDrafts', 'search', '');
  const [searchInput, setSearchInput] = useState(search);
  const runSearch = (value: string) => {
    setSearchInput(value.trim());
    setSearch(value.trim());
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDrafts(await listHiredEquipmentDrafts());
    } catch (err) {
      setDrafts([]);
      setError(describeLoadError(err, 'your drafts').message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openDraft = (formId: string) => {
    navigate(`/hired-equipment/grid?draft=${encodeURIComponent(formId)}`);
  };

  const discardDraft = async (formId: string) => {
    setDiscardingId(formId);
    setError(null);
    try {
      await discardHiredEquipmentDraft(formId);
      setDrafts((prev) => prev.filter((draft) => draft.formId !== formId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to discard draft');
    } finally {
      setDiscardingId(null);
    }
  };

  const columns: DataTableColumn<HiredEquipmentDraftSummary>[] = [
    {
      key: 'basis',
      header: 'Entry Basis',
      mobile: 'title',
      // V3-025 / ACT-01: the entry basis is the link that opens (continues) the draft.
      cell: (row) => (
        <IdLink onClick={() => openDraft(row.formId)}>{formatAnchorValue(row.anchor as HiredEquipmentAnchor)}</IdLink>
      ),
    },
    {
      key: 'method',
      header: 'Method',
      mobile: 'subtitle',
      cell: (row) => methodLabel(row.anchor.entryMethod),
    },
    {
      key: 'created',
      header: 'Created',
      mobileLabel: 'Created',
      cell: (row) => formatDate(row.createdAt),
    },
    {
      key: 'modified',
      header: 'Last Modified',
      mobileLabel: 'Last Modified',
      cell: (row) => formatDate(row.updatedAt),
    },
    {
      key: 'lines',
      header: 'Lines',
      align: 'right',
      mobileLabel: 'Lines',
      cell: (row) => <span className="tw-tabular-nums">{row.lineCount ?? '–'}</span>,
    },
    {
      key: 'value',
      header: 'Value',
      align: 'right',
      mobileLabel: 'Value',
      cell: (row) =>
        row.totalValue == null ? (
          '–'
        ) : (
          <span className="tw-font-semibold tw-tabular-nums">{formatCurrency(row.totalValue)}</span>
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      width: ACTIONS_COL_WIDTH,
      mobile: 'trailing',
      // V3-025 / ACT-02: Continue and Discard in one ⋮ – no inline edit / delete icons.
      cell: (row) => (
        <RowActions
          actions={[
            { kind: 'edit', label: 'Continue', onClick: () => openDraft(row.formId) },
            {
              kind: 'delete',
              label: 'Discard',
              disabled: discardingId === row.formId,
              onClick: () => setConfirmDiscardId(row.formId),
            },
          ]}
        />
      ),
    },
  ];

  const searchWords = search.toLowerCase().split(/\s+/).filter(Boolean);
  const shownDrafts = searchWords.length
    ? drafts.filter((row) => {
        const text = [
          formatAnchorValue(row.anchor as HiredEquipmentAnchor),
          methodLabel(row.anchor.entryMethod),
          formatDate(row.createdAt),
          formatDate(row.updatedAt),
        ]
          .join(' ')
          .toLowerCase();
        return searchWords.every((w) => text.includes(w));
      })
    : drafts;

  return (
    <>
      {/* No filters, only the search, so the panel is just its header bar (as on Roles). */}
      <FiltersCard
        title="Draft Filters"
        bodyClassName="tw-hidden"
        className="[&>[data-slot=card-header]]:tw-border-b-0"
        onClear={() => runSearch('')}
        hasActiveFilters={search !== ''}
        appliedFilterCount={0}
        search={{
          value: searchInput,
          onChange: setSearchInput,
          onSearch: runSearch,
          placeholder: 'Entry basis, method, or date',
          label: 'Search drafts',
        }}
      >
        {null}
      </FiltersCard>

      {error && (
        <Alert variant="danger" className="tw-mb-3">
          {error}
        </Alert>
      )}

      <DataTable
        columns={columns}
        data={shownDrafts}
        rowKey={(row) => row.formId}
        size="sm"
        striped
        bordered
        hover
        framed
        loading={loading}
        emptyMessage={
          search && drafts.length
            ? `No drafts match '${search}'.`
            : 'No saved drafts. Start one with New Hired Equipment.'
        }
      />

      <HeConfirmModal
        show={confirmDiscardId != null}
        title="Discard This Draft?"
        confirmLabel="Discard"
        confirmVariant="danger"
        cancelLabel="Keep Draft"
        onCancel={() => setConfirmDiscardId(null)}
        onConfirm={() => {
          const formId = confirmDiscardId;
          setConfirmDiscardId(null);
          if (formId) void discardDraft(formId);
        }}
      >
        <p className="tw-mb-0 tw-text-sm tw-text-content">
          The saved lines and photos will be deleted. This cannot be undone.
        </p>
      </HeConfirmModal>
    </>
  );
};

export default HiredEquipmentDrafts;
